import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, canonicalLinks, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl } from "@/lib/seo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import shopCounterPriceBoardImg from "@/assets/photos/shop-counter-price-board.webp";

const SLUG = "why-we-publish-our-prices";
const TITLE = "Why we publish our prices";
const DESCRIPTION =
  "Ajility publishes its full price ladder, from the free Operating Scan to every install and retainer. Why a pricing specialist hiding his prices says a lot.";
const CATEGORY = "Pricing";
const READ = "3 min read";

export const Route = createFileRoute("/insights/why-we-publish-our-prices")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage(`insights-${SLUG}`, TITLE),
      ogUrl(`/insights/${SLUG}`),
    ],
    links: canonicalLinks(`/insights/${SLUG}`),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: TITLE, path: `/insights/${SLUG}` }])),
      jsonLd(
        webPageSchema({
          path: `/insights/${SLUG}`,
          name: `${TITLE} | Ajility`,
          description: DESCRIPTION,
          dateModified: modifiedAt(SLUG),
          mainEntityId: `${absoluteUrl(`/insights/${SLUG}`)}#article`,
          image: `insights-${SLUG}`,
        }),
      ),
      jsonLd(
        articleSchema({
          title: TITLE,
          description: DESCRIPTION,
          slug: SLUG,
          datePublished: publishedAt(SLUG),
          dateModified: modifiedAt(SLUG),
          section: CATEGORY,
        }),
      ),
    ],
  }),
  component: WhyWePublishOurPrices,
});

function WhyWePublishOurPrices() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{CATEGORY}, {READ}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(SLUG)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{DESCRIPTION}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>


      <PhotoBand src={shopCounterPriceBoardImg} alt="A small trade business front counter with a printed price board mounted on the wall behind it." />

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <article className="article-body">
          <p>Go and find a management consultancy that publishes its prices.</p>
          <p>Take your time. We'll wait.</p>
          <p>Ajility publishes the complete ladder. The free Operating Scan. The Operating Diagnostic from $3,600 plus GST. The Full Diagnostic and 90-Day Roadmap from $6,900 plus GST for one entity. Every install and every retainer on the packages page, no "book a call to find out".</p>
          <p>Why: because pricing is literally the specialisation. A pricing consultant who hides his prices is telling you something.</p>
          <p>If your business runs on quotes and you'd like your own pricing to be this clear to your customers, that's exactly the work.</p>
          <p>
            <Link to="/packages" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">
              See packages and prices
            </Link>
          </p>
        </article>
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
            Want to know where your real bottleneck is?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
            Book a 30-minute fit call, a structured conversation rather than a pitch, about
            where the constraint actually sits in your business.
          </p>
          <BookFitCall
            placement={`insights_article_${SLUG}_footer`}
            buttonClassName="mt-8 inline-flex items-center gap-2 rounded-full bg-frost px-7 py-3.5 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <p className="mt-8 font-serif italic text-frost/80">
            "Strategy without execution is hallucination."
          </p>
        </div>
      </section>
    </div>
  );
}
